import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, Checkbox, SelectPicker } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import { AdvanceShareStyle, AdvanceShareStyleCategoryList, AdvanceShareStylePaginationFilterRequest, AdvanceShareStyleStatusList } from '@/models/advance-share-style.model';
import { getPagedAdvanceShareStyles } from '@/services/advance-share-style.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const AdvanceShareStyleList = () => {
  const [filterReq, setFilterReq] = useState<AdvanceShareStylePaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<AdvanceShareStyle[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: AdvanceShareStylePaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<AdvanceShareStyle> = await getPagedAdvanceShareStyles(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching advance share styles:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/advance-shares/styles/new`)}>
          Add New Style
        </Button>

        <Stack spacing={6}> 
          <SelectPicker
            label="Category"
            data={AdvanceShareStyleCategoryList}
            searchable={false}
            value={filterReq.category ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, category: dataKey ?? undefined }))} 
          />                    
          <SelectPicker
            label="Status"
            data={AdvanceShareStyleStatusList}
            searchable={false}
            value={filterReq.status ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, status: dataKey ?? undefined }))} 
          />                    
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Cover Image</HeaderCell>
          <ImageCell dataKey="coverImageUrl" />
        </Column>              
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Category</HeaderCell>
          <Cell>
            {(rowData: AdvanceShareStyle) => ( AdvanceShareStyleCategoryList.findLast(cat => cat.value == rowData.category)?.label )}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Status</HeaderCell>
          <Cell>
            {(rowData: AdvanceShareStyle) => ( AdvanceShareStyleStatusList.findLast(s => s.value == rowData.status)?.label )}
          </Cell>
        </Column>
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData : AdvanceShareStyle) => (              
              <IconButton onClick={() => nav(`/advance-shares/styles/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
      
    </PageContent>
  );
};

export default AdvanceShareStyleList;
